import { AppDispatch, RootState } from "redux/store";
import { CallLambda } from "./CallLambda";
import { mergeJobStates } from "redux/slices/user";
import { LoadJobsByIDsAction } from "./LoadJobsByIDs";
import Session from "redux/classes/Session";

/**
 * Lightweight 5s-poll path for the Model page Jobs table.
 *
 * Server returns a Query result over all in-scope jobs (cust_id + model_id +
 * user_id-if-non-admin) with just 4 fields per row: job_id, model_id,
 * job_status, result_name, end_time. The reducer merges those into existing
 * Job instances, drops jobs whose IDs don't appear in the response (deletion
 * detection), and surfaces any IDs the client doesn't have yet — those then
 * trigger a single follow-up LoadJobsByIDsAction so new rows can render with
 * their full field set (alias, name, etc.).
 *
 * Silently no-ops on transient errors so a flaky poll doesn't cascade into
 * a UI error state — next tick retries.
 */
export function LoadJobStatesAction(session: Session, modelID: string) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        if (!modelID) return;

        const payload = JSON.stringify({
            "action": "loadJobStates",
            "model_id": modelID,
        });

        let response: { status: number; data: any };
        try {
            response = await dispatch(CallLambda(session, "tableGrabber", payload));
        } catch {
            return;
        }

        if (response.status !== 200 || !Array.isArray(response.data?.states)) {
            return;
        }

        const states: Array<{
            job_id: string;
            model_id: string;
            job_status?: string;
            result_name?: string;
            end_time?: string;
        }> = response.data.states;

        // Diff: which IDs in the response are NEW to redux for this model?
        const existingForModel = new Set(
            getState().user.jobs.filter(j => j.modelID === modelID).map(j => j.jobID)
        );
        const newIDs = states
            .map(s => s.job_id)
            .filter(jid => jid && !existingForModel.has(jid));

        // Merge status updates + drop deletes for this model's slice.
        dispatch(mergeJobStates({ modelID, states }));

        // Fetch full rows for newly-discovered IDs (renders new table rows).
        if (newIDs.length > 0) {
            await dispatch(LoadJobsByIDsAction(session, newIDs)).catch(() => {});
        }
    };
}
